const REDIRECT_ERROR_CODE = "NEXT_REDIRECT";
const HTTP_ERROR_FALLBACK_ERROR_CODE = "NEXT_HTTP_ERROR_FALLBACK";
const HTTP_ERROR_FALLBACK_STATUSES = new Set(["401", "403", "404"]);
// Next's `RedirectStatusCode`: SeeOther, TemporaryRedirect, PermanentRedirect.
const REDIRECT_STATUSES = new Set(["303", "307", "308"]);

/**
 * Detects Next.js internal control-flow errors that are thrown as part of the
 * App Router's redirect() / notFound() / forbidden() / unauthorized()
 * mechanism, by message.
 *
 * These are not real application errors — Next.js uses thrown errors as a
 * signalling mechanism to trigger navigation. When a Server Action calls
 * redirect(), the error propagates to the client where Datadog RUM picks it up
 * as an unhandled error.
 *
 * `NEXT_NOT_FOUND` is the pre-Next 15 notFound() message. Next 15+ throws
 * notFound() / forbidden() / unauthorized() with the message
 * `NEXT_HTTP_ERROR_FALLBACK;<status>`.
 *
 * A router error raised during a Server Components render does NOT arrive with
 * either message: in production React replaces it with "Minified React error
 * #441" and the signal survives only on the error's `digest`. Use
 * `hasNextRouterErrorDigest` on the original error object for that case.
 *
 * @see https://nextjs.org/docs/app/api-reference/functions/redirect
 */
export const isNextRouterError = (message: string | undefined): boolean => {
  if (!message) {
    return false;
  }
  return (
    message === REDIRECT_ERROR_CODE ||
    message === "NEXT_NOT_FOUND" ||
    _isHttpErrorFallbackDigest(message)
  );
};

/**
 * Detects a Next.js router control-flow error by its `digest`, the same check
 * Next's own `isNextRouterError` makes (`isRedirectError` +
 * `isHTTPAccessFallbackError` in `next/dist/client/components`).
 *
 * This is what identifies a notFound() or redirect() thrown while rendering a
 * Server Component. React's production Flight client turns every server render
 * error into `Error("Minified React error #441")` and copies the server's
 * digest onto it, and Next's server hands router errors their own digest
 * (`NEXT_HTTP_ERROR_FALLBACK;404`, `NEXT_REDIRECT;replace;/path;307;`) rather
 * than a hash. A genuine render failure gets a numeric hash digest and is not
 * matched, so this cannot hide a real server error.
 */
export const hasNextRouterErrorDigest = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("digest" in error)) {
    return false;
  }

  const { digest } = error;
  if (typeof digest !== "string") {
    return false;
  }

  return _isHttpErrorFallbackDigest(digest) || _isRedirectDigest(digest);
};

const _isHttpErrorFallbackDigest = (value: string): boolean => {
  const [prefix, status, ...rest] = value.split(";");
  return (
    prefix === HTTP_ERROR_FALLBACK_ERROR_CODE &&
    status !== undefined &&
    HTTP_ERROR_FALLBACK_STATUSES.has(status) &&
    rest.length === 0
  );
};

// `NEXT_REDIRECT;<replace|push>;<url>;<status>;` — the url may itself contain
// semicolons, so only the fixed head and tail are checked.
const _isRedirectDigest = (value: string): boolean => {
  const parts = value.split(";");
  const [errorCode, type] = parts;
  const status = parts.at(-2);
  return (
    parts.length >= 5 &&
    errorCode === REDIRECT_ERROR_CODE &&
    (type === "replace" || type === "push") &&
    status !== undefined &&
    REDIRECT_STATUSES.has(status)
  );
};
